import { AxiosError } from 'axios';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { FormGeneratorOnSubmitParams } from 'react-form-builder';
import toast from 'react-hot-toast';
import { useAuth } from 'react-oidc-context';
import { Navigate, useSearchParams } from 'react-router-dom';
import { TaskPageBody } from '@/modules/common/components/TaskPageBody.tsx';
import ReauthenticationRequiredModal from '@/modules/common/components/modals/ReauthenticationRequiredModal.tsx';
import SuccessTaskCompletionModal from '@/modules/common/components/modals/SuccessTaskCompletionModal.tsx';
import { usePublicRegistersListQuery } from '@/modules/common/queries/registers.ts';
import {
  useGetPublicTaskQuery,
  usePublicResolveTaskMutation,
} from '@/modules/common/queries/tasks.ts';
import { commonPaths } from '@/modules/common/routes.tsx';
import { validateBase64TotalSize } from '@/modules/common/utils/file.ts';
import { ModalProviderContext, PageLayout, Spinner } from 'ui';
import { useIndexedDBState, useParseNumericQueryParam } from 'ui/hooks';
import { INDEXED_DB_STORES } from 'ui/utils';
import { RegisterResultRequest, TaskResult } from 'compliance-api';

const TaskPublic = () => {
  const [params] = useSearchParams();
  const auth = useAuth();

  const { openModal } = useContext(ModalProviderContext);

  const businessId = useParseNumericQueryParam('business_id');
  const association = params.get('association');
  const [, setFormData, clearFormData] = useIndexedDBState<FormGeneratorOnSubmitParams[]>(
    INDEXED_DB_STORES.TASK_FORM_DATA,
    `association-${association ?? '0'}-form-data`,
    [],
  );
  const { mutateAsync: resolveTask, isLoading: isTaskResolving } = usePublicResolveTaskMutation();

  const {
    hasNextPage: hasRegistersNextPage,
    fetchNextPage: fetchRegistersNextPage,
    data: registersData,
    isLoading: isLoadingRegisters,
    isFetchingNextPage: isFetchingRegistersNextPage,
  } = usePublicRegistersListQuery(
    {
      page: 1,
      page_size: 15,
      association: association || '',
      business_id: businessId ? +businessId : 0,
    },
    !association || !businessId,
  );

  useEffect(() => {
    if (hasRegistersNextPage && !isLoadingRegisters && !isFetchingRegistersNextPage) {
      fetchRegistersNextPage();
    }
  }, [
    fetchRegistersNextPage,
    hasRegistersNextPage,
    isFetchingRegistersNextPage,
    isLoadingRegisters,
  ]);

  const registers = useMemo(
    () => (registersData?.pages || []).flatMap(item => item?.data || []),
    [registersData?.pages],
  );

  const {
    data: taskData,
    isLoading: isTaskLoading,
    error: taskError,
  } = useGetPublicTaskQuery(
    { association: association || '', business_id: businessId ? +businessId : 0 },
    !association || !businessId,
  );
  const isAxiosError = taskError && taskError instanceof AxiosError;

  const isNotFoundError = isAxiosError && taskError?.response?.status === 404;

  const myOverdueTaskResultId = useMemo(() => {
    if (!taskData || !association) return null;
    return (
      (taskData.task.task_results || []).find(
        ({ result, association: associationX }) =>
          result === TaskResult.Due && associationX === association,
      )?.id || null
    );
  }, [association, taskData]);

  const handleResolveTask = useCallback(
    async (formResults: FormGeneratorOnSubmitParams[], registers: RegisterResultRequest[]) => {
      if (!association || !taskData?.task || !businessId) return;

      const { valid, totalMb } = validateBase64TotalSize(formResults, 89);

      if (!valid) {
        toast.error(
          `The total size of all uploaded files (${totalMb} MB) exceeds the allowed limit of 89 MB.`,
          {
            duration: 10000,
            id: 'file-size',
          },
        );
        return;
      }

      try {
        await resolveTask({
          business_id: businessId,
          result: TaskResult.Completed,
          form_results: { form_results: formResults, formItems: taskData.task?.form?.data || [] },
          association,
          registers,
          ...(myOverdueTaskResultId ? { task_result_id: myOverdueTaskResultId } : {}),
        });
        openModal(SuccessTaskCompletionModal, {
          taskName: taskData.task.name,
          onClick: () => {
            clearFormData();
          },
        });
        localStorage.removeItem(`association-${association}-form-data`);
      } catch (e) {
        const error = e as AxiosError;
        if (error?.response?.status === 418 || error?.response?.status === 401) {
          setFormData(formResults);
          localStorage.setItem(`association-${association}-form-data`, JSON.stringify(true));

          openModal(ReauthenticationRequiredModal, {
            onProceed: async () => {
              if (auth.isAuthenticated && error?.response?.status !== 401) {
                await auth.signoutRedirect({
                  post_logout_redirect_uri: `${window.location.origin}${commonPaths.static.taskForceLogin}?business_id=${businessId}&association=${association}`,
                });
              } else {
                await auth.signinRedirect({
                  redirect_uri: `${window.location.origin}/public/tasks?business_id=${businessId}&association=${association}`,
                });
              }
            },
          });

          return;
        }

        throw e;
      }
    },
    [
      association,
      taskData?.task,
      businessId,
      resolveTask,
      myOverdueTaskResultId,
      clearFormData,
      openModal,
      setFormData,
      auth,
    ],
  );

  if (isNotFoundError || !association || !businessId) {
    return <Navigate to={commonPaths.static.notFound} />;
  }

  if (isTaskLoading || !taskData?.task || isLoadingRegisters || isFetchingRegistersNextPage) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }
  return (
    <PageLayout
      title={
        <div className="flex gap-2 max-w-full w-[720px] m-auto">
          <span>{taskData?.task?.name}</span>
        </div>
      }
      headerClassname="[&>div>div]:w-full"
    >
      <TaskPageBody
        registers={registers}
        onResolveTask={handleResolveTask}
        task={taskData.task}
        isTaskResolving={isTaskResolving}
      />
    </PageLayout>
  );
};

export default TaskPublic;
